/**
 * Relationship edit buffer — pending predecessor/successor edits for a single
 * activity, held in React context and scoped to the active schedule.
 *
 * The buffer is the single source of truth; UI components read from it and
 * dispatch actions through the action hooks. It resets on schedule/scope
 * change or explicit Discard. Consumed by the Takt board edit modal, which
 * previews and applies the buffered edits through the orchestrator endpoints.
 */
import React from "react";
import { PredType } from "../../../../utils/p6";

export type EditOp =
  | {
      kind: "add";
      tempId: string;
      succ_id: number;
      pred_id: number;
      pred_type: PredType;
      lag_hours: number;
    }
  | {
      kind: "remove";
      edge_id: number;
      // Stashed so the banner can render the removed pred without re-querying.
      succ_id: number;
      pred_id: number;
    }
  | {
      kind: "update";
      edge_id: number;
      succ_id: number;
      pred_id: number;
      pred_type: PredType;
      lag_hours: number;
    };

export interface EditBufferState {
  edits: EditOp[];
}

const INITIAL: EditBufferState = {
  edits: [],
};

type Action =
  | { type: "addOp"; op: Extract<EditOp, { kind: "add" }> }
  | {
      type: "mutateAdd";
      tempId: string;
      patch: { pred_id?: number; pred_type?: PredType; lag_hours?: number };
    }
  | { type: "removeOp"; op: Extract<EditOp, { kind: "remove" }> }
  | { type: "updateOp"; op: Extract<EditOp, { kind: "update" }> }
  | { type: "revert"; matcher: (op: EditOp) => boolean }
  | { type: "discardAll" };

function reducer(state: EditBufferState, action: Action): EditBufferState {
  switch (action.type) {
    case "addOp":
      return { ...state, edits: [...state.edits, action.op] };
    case "mutateAdd":
      return {
        ...state,
        edits: state.edits.map((e) =>
          e.kind === "add" && e.tempId === action.tempId
            ? { ...e, ...action.patch }
            : e,
        ),
      };
    case "removeOp": {
      // If there was already an "add" for this pair (pending add → user removes
      // the not-yet-saved edge), drop the add instead of issuing a remove —
      // there's no edge_id to remove on the backend.
      const dropAdd = state.edits.findIndex(
        (e) =>
          e.kind === "add" &&
          e.succ_id === action.op.succ_id &&
          e.pred_id === action.op.pred_id,
      );
      if (dropAdd !== -1) {
        return {
          ...state,
          edits: state.edits.filter((_, i) => i !== dropAdd),
        };
      }
      // Drop any pending update on this edge — remove supersedes it.
      const cleaned = state.edits.filter(
        (e) => !(e.kind === "update" && e.edge_id === action.op.edge_id),
      );
      return { ...state, edits: [...cleaned, action.op] };
    }
    case "updateOp": {
      const without = state.edits.filter(
        (e) => !(e.kind === "update" && e.edge_id === action.op.edge_id),
      );
      return { ...state, edits: [...without, action.op] };
    }
    case "revert":
      return {
        ...state,
        edits: state.edits.filter((e) => !action.matcher(e)),
      };
    case "discardAll":
      return INITIAL;
    default:
      return state;
  }
}

interface EditBufferContextValue {
  state: EditBufferState;
  /** Emit a new pending add. Returns the tempId so the caller can mutate it later. */
  addPredecessor: (
    succ_id: number,
    pred_id: number,
    pred_type: PredType,
    lag_hours: number,
  ) => string;
  /** Mutate fields on an existing pending add by tempId. */
  mutatePendingAdd: (
    tempId: string,
    patch: { pred_id?: number; pred_type?: PredType; lag_hours?: number },
  ) => void;
  removeExistingEdge: (succ_id: number, pred_id: number, edge_id: number) => void;
  updateExistingEdge: (op: {
    edge_id: number;
    succ_id: number;
    pred_id: number;
    pred_type: PredType;
    lag_hours: number;
  }) => void;
  revertOp: (matcher: (op: EditOp) => boolean) => void;
  discardAll: () => void;
}

const EditBufferContext = React.createContext<EditBufferContextValue | null>(null);

export const EditBufferProvider: React.FC<{
  scheduleId: number | null;
  /** Optional discriminator for nested independent buffers (e.g. the Takt
   * edit modal mounts its own provider with `scopeKey={`modal-${activityId}`}`
   * to keep its draft separate from the Logic tab's). When the key changes,
   * the buffer resets — same effect as a schedule change. */
  scopeKey?: string;
  children: React.ReactNode;
}> = ({ scheduleId, scopeKey, children }) => {
  const [state, dispatch] = React.useReducer(reducer, INITIAL);

  // Buffer is per-(schedule, scope). When either changes, drop everything —
  // ids and edge_ids belong to the previous context and aren't valid.
  React.useEffect(() => {
    dispatch({ type: "discardAll" });
  }, [scheduleId, scopeKey]);

  const addPredecessor = React.useCallback(
    (
      succ_id: number,
      pred_id: number,
      pred_type: PredType,
      lag_hours: number,
    ): string => {
      const tempId = `add-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      dispatch({
        type: "addOp",
        op: { kind: "add", tempId, succ_id, pred_id, pred_type, lag_hours },
      });
      return tempId;
    },
    [],
  );

  const mutatePendingAdd = React.useCallback(
    (
      tempId: string,
      patch: { pred_id?: number; pred_type?: PredType; lag_hours?: number },
    ) => {
      dispatch({ type: "mutateAdd", tempId, patch });
    },
    [],
  );

  const removeExistingEdge = React.useCallback(
    (succ_id: number, pred_id: number, edge_id: number) => {
      dispatch({
        type: "removeOp",
        op: { kind: "remove", edge_id, succ_id, pred_id },
      });
    },
    [],
  );

  const updateExistingEdge = React.useCallback(
    (op: {
      edge_id: number;
      succ_id: number;
      pred_id: number;
      pred_type: PredType;
      lag_hours: number;
    }) => {
      dispatch({ type: "updateOp", op: { kind: "update", ...op } });
    },
    [],
  );

  const revertOp = React.useCallback((matcher: (op: EditOp) => boolean) => {
    dispatch({ type: "revert", matcher });
  }, []);

  const discardAll = React.useCallback(() => {
    dispatch({ type: "discardAll" });
  }, []);

  const value = React.useMemo<EditBufferContextValue>(
    () => ({
      state,
      addPredecessor,
      mutatePendingAdd,
      removeExistingEdge,
      updateExistingEdge,
      revertOp,
      discardAll,
    }),
    [
      state,
      addPredecessor,
      mutatePendingAdd,
      removeExistingEdge,
      updateExistingEdge,
      revertOp,
      discardAll,
    ],
  );

  return (
    <EditBufferContext.Provider value={value}>
      {children}
    </EditBufferContext.Provider>
  );
};

export function useEditBuffer(): EditBufferContextValue {
  const ctx = React.useContext(EditBufferContext);
  if (ctx === null) {
    throw new Error("useEditBuffer must be used inside an EditBufferProvider");
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/** Pending edits where activity appears as the SUCCESSOR — used by the
 * predecessors-panel editor. */
export function selectEditsAsSucc(
  edits: EditOp[],
  activityId: number,
): EditOp[] {
  return edits.filter((e) => e.succ_id === activityId);
}

/** Pending edits where activity appears as the PREDECESSOR — used by the
 * successors-panel editor. */
export function selectEditsAsPred(
  edits: EditOp[],
  activityId: number,
): EditOp[] {
  return edits.filter((e) => e.pred_id === activityId);
}

/** Pending update for a given edge_id, or undefined. */
export function selectPendingUpdate(
  edits: EditOp[],
  edge_id: number,
): Extract<EditOp, { kind: "update" }> | undefined {
  return edits.find(
    (e): e is Extract<EditOp, { kind: "update" }> =>
      e.kind === "update" && e.edge_id === edge_id,
  );
}

/** True if the given edge_id is queued for removal. */
export function selectIsRemoved(edits: EditOp[], edge_id: number): boolean {
  return edits.some((e) => e.kind === "remove" && e.edge_id === edge_id);
}
